import { handleApiError, handleApiResponse } from "@/methods/api";
import {
  renumberQuestionSerialsAcrossSections,
  toActivityQuestionCreate,
} from "@/methods/activity";
import ApiVerified from "@/services/apiVerified";
import { ApiResponse } from "@/types/global";
import {
  AvailableQuestion,
  ConductInteractiveVideo,
  EndInteractiveVideoInputs,
  InteractiveVideoAdminDisplay,
  InteractiveVideoAdminReportEntry,
  InteractiveVideoAllInputs,
  InteractiveVideoCreateInputs,
  InteractiveVideoDesignDisplay,
  InteractiveVideoDesignInputs,
  InteractiveVideoDesignWireInputs,
  InteractiveVideoSection,
  InteractiveVideoSectionInput,
  PushInteractiveVideoEventsInputs,
  SaveInteractiveVideoQuestionsInputs,
  SaveInteractiveVideoQuestionsWireInputs,
  StudentInteractiveVideoDisplay,
  StudentInteractiveVideoReport,
  StudentInteractiveVideoReportDetails,
  SubmitVideoSectionAnswersInputs,
  VideoConceptMappingEntry,
  VideoSectionResult,
} from "@/types/interactiveVideo";

// ──────────────────────────────────────────────
// Save-boundary wire mappers (AR-9.2 / AR-9.3)
// Types don't strip fields from an axios body — the runtime object is serialized as-is. These
// explicit-key mappers build the exact interactive-video.json wire shape so create/save/update
// never over-post the working-only `InteractiveVideoId` or the view-model question axes.
// ──────────────────────────────────────────────

// Working section → interactive-video.json InteractiveVideoSectionInput. Reuses the Phase-7
// question mapper (drops SubjectId/SubjectChapterId/TopicId/ConceptId, keeps TopicConceptId).
function toInteractiveVideoSectionInput(
  section: InteractiveVideoSection,
): InteractiveVideoSectionInput {
  const { Questions, ...sectionFields } = section;
  return {
    ...sectionFields,
    Questions: Questions ? Questions.map(toActivityQuestionCreate) : null,
  };
}

// Working create design → interactive-video.json InteractiveVideoAllInputs (create body).
// AR-9.2: strips the working-only InteractiveVideoId; AR-9.3: maps sections to the wire rows.
function toInteractiveVideoCreateInputs(
  inputs: InteractiveVideoAllInputs,
): InteractiveVideoCreateInputs {
  const { InteractiveVideoId, Sections, ...rest } = inputs;
  void InteractiveVideoId; // working-only field, never part of the create wire body (AR-9.2)
  return {
    ...rest,
    Sections: renumberQuestionSerialsAcrossSections(Sections.map(toInteractiveVideoSectionInput)),
  };
}

// Working update design → interactive-video.json InteractiveVideoDesignInputs (update body).
function toInteractiveVideoDesignWireInputs(
  inputs: InteractiveVideoDesignInputs,
): InteractiveVideoDesignWireInputs {
  const { Sections, ...rest } = inputs;
  return {
    ...rest,
    Sections: renumberQuestionSerialsAcrossSections(Sections.map(toInteractiveVideoSectionInput)),
  };
}

// Working save-questions input → interactive-video.json SaveInteractiveVideoQuestionsInputs.
function toSaveInteractiveVideoQuestionsWire(
  inputs: SaveInteractiveVideoQuestionsInputs,
): SaveInteractiveVideoQuestionsWireInputs {
  const { Sections, ...rest } = inputs;
  return {
    ...rest,
    Sections: renumberQuestionSerialsAcrossSections(Sections.map(toInteractiveVideoSectionInput)),
  };
}

export default class InteractiveVideoApi extends ApiVerified {
  async createInteractiveVideo(inputs: InteractiveVideoAllInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(
        `interactive-video/create-interactive-video`,
        toInteractiveVideoCreateInputs(inputs),
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getInteractiveVideoDesign(interactiveVideoId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<InteractiveVideoDesignDisplay>(
        `interactive-video/get-interactive-video-design`,
        { params: { interactiveVideoId: interactiveVideoId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getVideoConceptMapping(videoLibraryId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<VideoConceptMappingEntry>>(
        `interactive-video/get-video-concept-mapping`,
        { params: { videoLibraryId: videoLibraryId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAvailableQuestions(
    subjectChapterId: number,
    questionTypeIdsCsv: string,
    chapterTopicIdsCsv?: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AvailableQuestion>>(
        `interactive-video/get-available-questions`,
        {
          params: {
            subjectChapterId: subjectChapterId,
            questionTypeIdsCsv: questionTypeIdsCsv,
            // AR-9.1: doc param is `chapterTopicIdsCsv` (was `topicIdsCsv` — never applied).
            chapterTopicIdsCsv: chapterTopicIdsCsv || undefined,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async autoGenerateSections(videoLibraryId: number, courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<InteractiveVideoSection>>(
        `interactive-video/auto-generate-sections`,
        null,
        { params: { videoLibraryId: videoLibraryId, courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveInteractiveVideoQuestions(
    inputs: SaveInteractiveVideoQuestionsInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `interactive-video/save-interactive-video-questions`,
        toSaveInteractiveVideoQuestionsWire(inputs),
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async finalizeInteractiveVideo(interactiveVideoId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `interactive-video/finalize-interactive-video`,
        null,
        { params: { interactiveVideoId: interactiveVideoId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getInteractiveVideosForAdmin(courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<InteractiveVideoAdminDisplay>>(
        `interactive-video/get-interactive-videos-for-admin`,
        { params: { courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateInteractiveVideo(
    interactiveVideoId: number,
    inputs: InteractiveVideoDesignInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `interactive-video/update-interactive-video`,
        toInteractiveVideoDesignWireInputs(inputs),
        { params: { interactiveVideoId: interactiveVideoId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteInteractiveVideo(interactiveVideoId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `interactive-video/delete-interactive-video`,
        { params: { interactiveVideoId: interactiveVideoId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentInteractiveVideos(courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudentInteractiveVideoDisplay>>(
        `interactive-video/get-student-interactive-videos`,
        { params: { courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getConductInteractiveVideo(interactiveVideoId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ConductInteractiveVideo>(
        `interactive-video/get-conduct-interactive-video`,
        { params: { interactiveVideoId: interactiveVideoId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async startInteractiveVideo(
    interactiveVideoId: number,
    activityInstanceId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `interactive-video/start-interactive-video`,
        null,
        {
          params: {
            interactiveVideoId: interactiveVideoId,
            activityInstanceId: activityInstanceId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async pushInteractiveVideoEvents(
    inputs: PushInteractiveVideoEventsInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `interactive-video/push-interactive-video-events`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async submitSectionAnswers(inputs: SubmitVideoSectionAnswersInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<VideoSectionResult>(
        `interactive-video/submit-section-answers`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async endInteractiveVideo(inputs: EndInteractiveVideoInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `interactive-video/end-interactive-video`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async resetInteractiveVideo(interactiveVideoId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `interactive-video/reset-interactive-video`,
        { params: { interactiveVideoId: interactiveVideoId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentInteractiveVideoByQrContent(
    qrCategoryId: number,
    contentId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<StudentInteractiveVideoDisplay>(
        `interactive-video/student-interactive-video-by-qr-content`,
        { params: { qrCategoryId: qrCategoryId, contentId: contentId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentReport(activityInstanceId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<StudentInteractiveVideoReport>(
        `interactive-video/get-student-report`,
        { params: { activityInstanceId: activityInstanceId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentReportDetails(activityInstanceId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<StudentInteractiveVideoReportDetails>(
        `interactive-video/get-student-report-details`,
        { params: { activityInstanceId: activityInstanceId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAdminReport(interactiveVideoId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<InteractiveVideoAdminReportEntry>>(
        `interactive-video/get-admin-report`,
        { params: { interactiveVideoId: interactiveVideoId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
